import { FC, useEffect, useMemo, useState } from 'react';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { InputText } from 'primereact/inputtext';
import { Check, CheckSquare, Plus, X } from 'lucide-react';
import useGetUsersBySite from '../../../custom-hooks/useGetUsersBySite';
import { useAppSelector } from '../../../redux/hooks';
import { selectUser } from '../../../redux/reducers/userSlice';
import { Roles } from '../../../models/User';
import { isUserAdmin } from '../../../businessLogicService/isUserAdmin';
import { ThreeDotLoader } from '../../Fallback/ThreeDotsLoader';
import { WizardUser } from './wizardTypes';

const TEACHER_ROLE_ID = 'RoleNode:3';
const ADMIN_ROLES: string[] = [Roles.SITE_ADMIN, Roles.NETWORK_ADMIN];
const PAGE_SIZE = 25;

interface WizardTeacher extends WizardUser {}

interface Props {
  siteId: string;
  selected: WizardUser[];
  onChange: (coTeachers: WizardUser[]) => void;
  onNewTeacher?: () => void;
  /** Teachers created in this session that won't appear in completeUsers until the roster query refetches. */
  extraTeachers?: WizardUser[];
}

const ClassWizardCoTeacherStep: FC<Props> = ({ siteId, selected, onChange, onNewTeacher, extraTeachers = [] }) => {
  const userRedux = useAppSelector(selectUser);
  const isAdmin = isUserAdmin(userRedux);
  const [search, setSearch] = useState('');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const { completeUsers, loading } = useGetUsersBySite({ roleIds: [TEACHER_ROLE_ID] });

  // Teachers active at the class site, excluding the current user (the owner) and any admins.
  const teachers = useMemo<WizardTeacher[]>(() => {
    const ownerId = `UserNode:${userRedux?.id}`;
    const fromSite = completeUsers
      .filter(
        user =>
          user.id !== ownerId &&
          user.site?.some(site => site.id === siteId) &&
          !user.role?.some(role => ADMIN_ROLES.includes(role.name)),
      )
      .map(user => ({ id: user.id, firstName: user.firstName, lastName: user.lastName }));
    // A teacher created in this session won't appear in completeUsers until the roster
    // query refetches, so merge them in directly (regardless of checked state, so
    // unchecking one doesn't make it vanish) and surface them first so the person who
    // just created them can see them right away.
    const newlyCreated = extraTeachers.filter(teacher => !fromSite.some(user => user.id === teacher.id));
    return [...newlyCreated, ...fromSite.sort((a, b) => a.lastName.localeCompare(b.lastName))];
  }, [completeUsers, siteId, userRedux?.id, extraTeachers]);

  const hasAnyTeachers = teachers.length > 0;

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return teachers;
    return teachers.filter(t => `${t.firstName} ${t.lastName}`.toLowerCase().includes(term));
  }, [teachers, search]);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [search]);

  const visible = filtered.slice(0, visibleCount);

  const handleListScroll = (event: React.UIEvent<HTMLUListElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 48 && visibleCount < filtered.length) {
      setVisibleCount(count => count + PAGE_SIZE);
    }
  };

  const toggle = (teacher: WizardUser, checked: boolean) => {
    onChange(checked ? [...selected, teacher] : selected.filter(t => t.id !== teacher.id));
  };

  const selectAll = () => {
    const byId = new Map(selected.map(t => [t.id, t]));
    filtered.forEach(t => byId.set(t.id, t));
    onChange(Array.from(byId.values()));
  };

  const clearAll = () => onChange([]);

  if (loading) {
    return (
      <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-[20rem] tw-w-full">
        <ThreeDotLoader dark />
        <p className="tw-text-neutral-800 tw-font-semibold tw-mb-3 tw-text-xl">Loading teachers...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-column gap-md py-2">
      <div className="flex justify-content-between align-items-center gap-md">
        {selected.length > 0 ? (
          <span className="flex align-items-center gap-sm">
            <span className="tw-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-rounded-full tw-bg-emerald-100">
              <Check size={12} className="tw-text-[#007A54]" aria-hidden />
            </span>
            <span className="text-lg font-bold" style={{ color: '#393939' }}>
              {selected.length} selected teacher{selected.length === 1 ? '' : 's'}
            </span>
          </span>
        ) : (
          <span className="flex align-items-center gap-sm text-color-secondary">
            <span className="tw-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-rounded-[5.33px] tw-bg-neutral-100">
              <Check size={12} className="tw-text-[#A6A6A6]" aria-hidden />
            </span>
            <span
              className="text-lg font-bold"
              style={{ fontFamily: "'Atkinson Hyperlegible', sans-serif", lineHeight: '1.5rem' }}
            >
              No selected teacher
            </span>
          </span>
        )}
        {isAdmin && (
          <Button label="Add new teacher" icon={<Plus size={14} />} outlined rounded onClick={onNewTeacher} />
        )}
      </div>

      <IconField iconPosition="left" className="nc_classes-search w-full">
        <InputIcon className="nc_icon-nc_search icon-sm"> </InputIcon>
        <InputText
          value={search}
          onChange={event => setSearch(event.target.value)}
          placeholder="Search teachers..."
          className="w-full"
          aria-label="Search teachers"
        />
      </IconField>

      <div className="nc_wizard-list-box flex flex-column">
        {hasAnyTeachers && (
          <div className="flex justify-content-between align-items-center nc_wizard-list-box__actions">
            <Button
              label="Select all"
              icon={<CheckSquare size={14} />}
              link
              className="nc_wizard-list-box__action-btn"
              onClick={selectAll}
              disabled={!filtered.length}
            />
            <Button
              label="Clear all"
              icon={<X size={14} />}
              link
              className="nc_wizard-list-box__clear nc_wizard-list-box__action-btn"
              onClick={clearAll}
              disabled={!selected.length}
            />
          </div>
        )}
        {filtered.length ? (
          <ul className="nc_wizard-user-list" onScroll={handleListScroll}>
            {visible.map(teacher => {
              const checked = selected.some(t => t.id === teacher.id);
              return (
                <li key={teacher.id} className="flex align-items-center justify-content-between">
                  <label
                    className={`flex align-items-center gap-md cursor-pointer flex-grow-1 nc_wizard-user-list__row${
                      checked ? ' nc_wizard-user-list__row--selected' : ''
                    }`}
                  >
                    <Checkbox checked={checked} onChange={event => toggle(teacher, Boolean(event.checked))} />
                    {teacher.firstName} {teacher.lastName}
                  </label>
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="nc_wizard-empty-list flex flex-column align-items-center justify-content-center gap-sm text-center text-color-secondary">
            {hasAnyTeachers ? (
              `No match for "${search}"`
            ) : isAdmin ? (
              'No teachers found.'
            ) : (
              <span>
                <b>No teachers found.</b>
                <br />
                Admins can add teachers from the Teachers page.
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClassWizardCoTeacherStep;
